/**
 * Schemas Zod para validación de datos fiscales argentinos
 *
 * Este archivo centraliza las validaciones fiscales reutilizables:
 * - CUIT/CUIL
 * - CAE (Código de Autorización Electrónico)
 * - Alícuotas IVA
 * - Tipos de comprobante
 * - Condición IVA
 * - Período fiscal
 * - Punto de venta
 * - Número de comprobante
 *
 * @see .claude/rules/contable.md para reglas de negocio
 */

import { z } from 'zod'
import { validarCUIT } from '@/lib/utils'

// ===========================================
// CUIT / CUIL
// ===========================================

/**
 * Schema para CUIT argentino
 * - Acepta formato con o sin guiones (XX-XXXXXXXX-X o XXXXXXXXXXX)
 * - Valida longitud (11 dígitos)
 * - Valida dígito verificador usando algoritmo AFIP
 * - Valida tipo (20, 23, 24, 27, 30, 33, 34)
 */
export const cuitSchema = z
  .string()
  .min(1, 'El CUIT es requerido')
  .refine(
    (val) => {
      const limpio = val.replace(/\D/g, '')
      return limpio.length === 11
    },
    { message: 'El CUIT debe tener 11 dígitos' }
  )
  .refine(
    (val) => {
      const limpio = val.replace(/\D/g, '')
      if (limpio.length !== 11) return false
      return validarCUIT(limpio).valido
    },
    { message: 'El CUIT no es válido (dígito verificador incorrecto)' }
  )

/**
 * Schema para CUIT opcional
 * Útil para campos donde el CUIT no es requerido pero si se ingresa debe ser válido
 */
export const cuitOptionalSchema = z
  .string()
  .nullable()
  .optional()
  .refine(
    (val) => {
      if (!val || val.trim() === '') return true
      const limpio = val.replace(/\D/g, '')
      return limpio.length === 11
    },
    { message: 'El CUIT debe tener 11 dígitos' }
  )
  .refine(
    (val) => {
      if (!val || val.trim() === '') return true
      const limpio = val.replace(/\D/g, '')
      if (limpio.length !== 11) return false
      return validarCUIT(limpio).valido
    },
    { message: 'El CUIT no es válido' }
  )

// ===========================================
// CAE (Código de Autorización Electrónico)
// ===========================================

/**
 * Schema para CAE de AFIP
 * - Exactamente 14 dígitos numéricos
 */
export const caeSchema = z
  .string()
  .regex(/^\d{14}$/, 'El CAE debe tener exactamente 14 dígitos')

/**
 * Schema para CAE opcional (comprobantes sin CAE aún)
 */
export const caeOptionalSchema = z
  .string()
  .nullable()
  .optional()
  .refine(
    (val) => {
      if (!val || val.trim() === '') return true
      return /^\d{14}$/.test(val)
    },
    { message: 'El CAE debe tener exactamente 14 dígitos' }
  )

// ===========================================
// ALÍCUOTAS IVA
// ===========================================

/**
 * IDs de alícuotas IVA según AFIP
 * @see src/workspaces/contable/types/comprobantes.ts ALICUOTAS_IVA
 */
export const AlicuotaIVAId = {
  NO_GRAVADO: 1,
  EXENTO: 2,
  CERO: 3,
  IVA_10_5: 4,
  IVA_21: 5,
  IVA_27: 6,
  IVA_5: 8,
  IVA_2_5: 9,
} as const

export type AlicuotaIVAIdType = (typeof AlicuotaIVAId)[keyof typeof AlicuotaIVAId]

/**
 * Schema para ID de alícuota IVA (código AFIP)
 */
export const alicuotaIvaIdSchema = z.union([
  z.literal(1),  // No gravado
  z.literal(2),  // Exento
  z.literal(3),  // 0%
  z.literal(4),  // 10.5%
  z.literal(5),  // 21%
  z.literal(6),  // 27%
  z.literal(8),  // 5%
  z.literal(9),  // 2.5%
])

/**
 * Schema para valor de alícuota IVA (porcentaje)
 */
export const alicuotaIvaValorSchema = z.union([
  z.literal(0),
  z.literal(2.5),
  z.literal(5),
  z.literal(10.5),
  z.literal(21),
  z.literal(27),
])

/**
 * Schema para alícuota IVA como string (para selects)
 */
export const alicuotaIvaStringSchema = z.enum([
  '21',
  '10.5',
  '27',
  '5',
  '2.5',
  '0',
  'exento',
  'no_gravado',
])

export type AlicuotaIVAString = z.infer<typeof alicuotaIvaStringSchema>

// ===========================================
// TIPOS DE COMPROBANTE
// ===========================================

/**
 * Schema para tipo de comprobante AFIP
 */
export const tipoComprobanteSchema = z.enum([
  'FA', 'FB', 'FC',      // Facturas
  'NCA', 'NCB', 'NCC',   // Notas de Crédito
  'NDA', 'NDB', 'NDC',   // Notas de Débito
])

export type TipoComprobante = z.infer<typeof tipoComprobanteSchema>

/**
 * Schema para letra de comprobante
 */
export const letraComprobanteSchema = z.enum(['A', 'B', 'C'])

export type LetraComprobante = z.infer<typeof letraComprobanteSchema>

/**
 * Schema para estado de comprobante
 */
export const estadoComprobanteSchema = z.enum([
  'borrador',
  'emitido',
  'anulado',
  'rechazado',
])

export type EstadoComprobante = z.infer<typeof estadoComprobanteSchema>

/**
 * Schema para tipo de operación
 */
export const tipoOperacionSchema = z.enum(['venta', 'compra'])

export type TipoOperacion = z.infer<typeof tipoOperacionSchema>

/**
 * Schema para concepto de comprobante AFIP
 * 1 = Productos, 2 = Servicios, 3 = Ambos
 */
export const conceptoComprobanteSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
])

export type ConceptoComprobante = z.infer<typeof conceptoComprobanteSchema>

// ===========================================
// CONDICIÓN IVA
// ===========================================

/**
 * Schema para condición frente al IVA
 */
export const condicionIvaSchema = z.enum([
  'responsable_inscripto',
  'monotributista',
  'exento',
  'consumidor_final',
  'no_categorizado',
  'iva_liberado',
  'responsable_no_inscripto',
  'proveedor_exterior',
  'cliente_exterior',
  'monotributista_social',
  'monotributista_promovido',
])

export type CondicionIVA = z.infer<typeof condicionIvaSchema>

/**
 * Schema para condición IVA opcional (para selects con valor vacío)
 */
export const condicionIvaOptionalSchema = condicionIvaSchema.nullable().optional()

// ===========================================
// RÉGIMEN IIBB
// ===========================================

/**
 * Schema para régimen de Ingresos Brutos
 */
export const regimenIIBBSchema = z.enum(['local', 'convenio_multilateral'])

export type RegimenIIBB = z.infer<typeof regimenIIBBSchema>

// ===========================================
// PERÍODO FISCAL
// ===========================================

/**
 * Schema para período fiscal (formato YYYY-MM)
 * Ejemplo: "2026-01"
 */
export const periodoFiscalSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato de período inválido (YYYY-MM)')

/**
 * Schema para año fiscal (4 dígitos)
 */
export const anioFiscalSchema = z
  .number()
  .int()
  .min(1990, 'El año debe ser mayor a 1990')
  .max(2100, 'El año debe ser menor a 2100')

/**
 * Schema para mes fiscal (1-12)
 */
export const mesFiscalSchema = z.number().int().min(1).max(12)

// ===========================================
// PUNTO DE VENTA
// ===========================================

/**
 * Schema para número de punto de venta AFIP
 * - Número entero de 1 a 99999
 */
export const puntoVentaSchema = z
  .number()
  .int('El punto de venta debe ser un número entero')
  .min(1, 'El punto de venta debe ser mayor a 0')
  .max(99999, 'El punto de venta no puede superar 99999')

/**
 * Schema para punto de venta como string (para inputs)
 */
export const puntoVentaStringSchema = z
  .string()
  .regex(/^\d{1,5}$/, 'Punto de venta inválido')
  .transform((val) => parseInt(val, 10))

// ===========================================
// NÚMERO DE COMPROBANTE
// ===========================================

/**
 * Schema para número de comprobante AFIP
 * - Número entero de 1 a 99999999
 */
export const numeroComprobanteSchema = z
  .number()
  .int('El número debe ser un número entero')
  .min(1, 'El número debe ser mayor a 0')
  .max(99999999, 'El número no puede superar 99999999')

/**
 * Schema para número de comprobante formateado (0001-00000001)
 */
export const numeroComprobanteFormateadoSchema = z
  .string()
  .regex(
    /^\d{4}-\d{8}$/,
    'Formato inválido. Usar: XXXX-XXXXXXXX (ej: 0001-00000001)'
  )

// ===========================================
// MONEDA
// ===========================================

/**
 * Schema para moneda AFIP
 */
export const monedaSchema = z.enum(['PES', 'USD', 'EUR'])

export type Moneda = z.infer<typeof monedaSchema>

/**
 * Schema para tipo de cambio
 */
export const tipoCambioSchema = z
  .number()
  .positive('El tipo de cambio debe ser positivo')
  .default(1)

// ===========================================
// IMPORTES
// ===========================================

/**
 * Schema para importe monetario (2 decimales)
 */
export const importeSchema = z
  .number()
  .nonnegative('El importe no puede ser negativo')
  .multipleOf(0.01, 'Máximo 2 decimales')

/**
 * Schema para importe que puede ser negativo (NC/ND)
 */
export const importeConSignoSchema = z
  .number()
  .multipleOf(0.01, 'Máximo 2 decimales')

// ===========================================
// TIPOS DOCUMENTO RECEPTOR
// ===========================================

/**
 * IDs de tipos de documento según AFIP
 */
export const TipoDocumentoAFIP = {
  CUIT: 80,
  CUIL: 86,
  CDI: 87,
  LE: 89,
  LC: 90,
  CI_EXTRANJERA: 91,
  DNI: 96,
  PASAPORTE: 94,
  CONSUMIDOR_FINAL: 99,
} as const

/**
 * Schema para tipo de documento AFIP
 */
export const tipoDocumentoAfipSchema = z.union([
  z.literal(80),  // CUIT
  z.literal(86),  // CUIL
  z.literal(87),  // CDI
  z.literal(89),  // LE
  z.literal(90),  // LC
  z.literal(91),  // CI Extranjera
  z.literal(94),  // Pasaporte
  z.literal(96),  // DNI
  z.literal(99),  // Consumidor Final
])

export type TipoDocumentoAFIP = z.infer<typeof tipoDocumentoAfipSchema>

// ===========================================
// SCHEMAS COMPUESTOS
// ===========================================

/**
 * Schema para datos fiscales mínimos de un cliente/proveedor
 */
export const datosFiscalesBasicosSchema = z.object({
  cuit: cuitSchema,
  condicion_iva: condicionIvaSchema,
  razon_social: z.string().min(1, 'La razón social es requerida'),
})

/**
 * Schema para identificación de comprobante (para búsquedas/referencias)
 */
export const identificacionComprobanteSchema = z.object({
  tipo: tipoComprobanteSchema,
  letra: letraComprobanteSchema,
  punto_venta: puntoVentaSchema,
  numero: numeroComprobanteSchema,
})

/**
 * Schema para período de fechas (rango)
 */
export const periodoFechasSchema = z
  .object({
    desde: z.string().datetime({ offset: true }).or(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
    hasta: z.string().datetime({ offset: true }).or(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
  })
  .refine(
    (data) => new Date(data.desde) <= new Date(data.hasta),
    { message: 'La fecha desde debe ser anterior o igual a la fecha hasta' }
  )

// ===========================================
// HELPERS DE VALIDACIÓN
// ===========================================

/**
 * Valida y formatea un CUIT
 * @returns CUIT formateado (XX-XXXXXXXX-X) o null si es inválido
 */
export function formatearYValidarCUIT(cuit: string): string | null {
  const result = cuitSchema.safeParse(cuit)
  if (!result.success) return null

  const limpio = cuit.replace(/\D/g, '')
  return `${limpio.slice(0, 2)}-${limpio.slice(2, 10)}-${limpio.slice(10)}`
}

/**
 * Obtiene el valor de alícuota IVA desde el ID AFIP
 */
export function obtenerValorAlicuota(id: AlicuotaIVAIdType): number {
  const valores: Record<AlicuotaIVAIdType, number> = {
    1: 0,      // No gravado
    2: 0,      // Exento
    3: 0,      // 0%
    4: 10.5,   // 10.5%
    5: 21,     // 21%
    6: 27,     // 27%
    8: 5,      // 5%
    9: 2.5,    // 2.5%
  }
  return valores[id]
}

/**
 * Obtiene el ID AFIP desde el valor de alícuota
 */
export function obtenerIdAlicuota(valor: number): AlicuotaIVAIdType {
  const ids: Record<number, AlicuotaIVAIdType> = {
    0: 3,      // 0% (hay que distinguir exento/no gravado por contexto)
    2.5: 9,
    5: 8,
    10.5: 4,
    21: 5,
    27: 6,
  }
  return ids[valor] ?? 5 // Default 21%
}

// ===========================================
// RE-EXPORTAR TIPOS
// ===========================================

export type DatosFiscalesBasicos = z.infer<typeof datosFiscalesBasicosSchema>
export type IdentificacionComprobante = z.infer<typeof identificacionComprobanteSchema>
export type PeriodoFechas = z.infer<typeof periodoFechasSchema>
